/**
 * Shared camera-capture helpers used by both the persistent packing recorder
 * (`packing-recorder-context`) and the ad-hoc webcam modal.
 *
 * These exist to make capture work consistently across browsers — in
 * particular Safari on macOS / iOS (WebKit), which does NOT support the WebM
 * container that Chromium uses for `MediaRecorder`. Forcing `video/webm` there
 * throws on construction; the correct behaviour is to let WebKit fall back to
 * its native `video/mp4`.
 */

/**
 * Choose a `MediaRecorder` mime type the current browser actually supports.
 *
 * Chromium/Firefox report WebM (best quality first); WebKit reports MP4 — but
 * older Safari builds have an unreliable `isTypeSupported` that answers `false`
 * for everything even though recording works. In that case we return `""` so
 * the caller constructs `MediaRecorder` WITHOUT a forced mime type and the
 * browser picks its own native container (MP4 on Safari) instead of being
 * handed an unsupported `video/webm` that throws.
 */
export function pickVideoMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4;codecs=h264,aac",
    "video/mp4;codecs=h264",
    "video/mp4",
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      // Some engines throw on unusual codec strings — treat as unsupported.
    }
  }
  return "";
}

/**
 * Construct a `MediaRecorder`, preferring the requested mime type + bitrate but
 * gracefully degrading. Returns `null` only when the browser cannot record at
 * all (so the caller can surface a single "not supported" message and release
 * the stream).
 *
 * Crucially, the bitrate is preserved on the fallback path (unlike a bare
 * `new MediaRecorder(stream)`), so a forced-mime failure on Safari doesn't also
 * silently drop the quality setting.
 */
export function createVideoRecorder(
  stream: MediaStream,
  mimeType: string,
  videoBitsPerSecond = 2_500_000,
): MediaRecorder | null {
  if (typeof MediaRecorder === "undefined") return null;
  const opts: MediaRecorderOptions = { videoBitsPerSecond };
  if (mimeType) opts.mimeType = mimeType;
  try {
    return new MediaRecorder(stream, opts);
  } catch {
    // Forced mime/bitrate rejected — keep the bitrate, drop the mime.
    try {
      return new MediaRecorder(stream, { videoBitsPerSecond });
    } catch {
      // Last resort: let the browser choose everything.
      try {
        return new MediaRecorder(stream);
      } catch {
        return null;
      }
    }
  }
}

/**
 * Errors that mean a requested track (camera OR microphone) could not be
 * provided. `getUserMedia` rejects the WHOLE request if ANY requested track
 * fails, so when these occur with `audio: true` the blocker is frequently the
 * MICROPHONE, not the camera.
 */
const MISSING_DEVICE_ERRORS = new Set([
  "NotFoundError",
  "DevicesNotFoundError",
  "NotReadableError",
  "TrackStartError",
]);

/**
 * `getUserMedia` with resilient fallbacks for real-world capture hardware.
 *
 * Two distinct failures are recovered from:
 *
 * 1. **Over-constrained video** — desktop/external webcams (e.g. a USB camera)
 *    can't always satisfy an exact resolution or a `facingMode` hint. We drop
 *    the hints and retry with a plain `video: true` request (keeping the audio
 *    intent).
 *
 * 2. **No microphone** — a machine can have a working camera but no audio input
 *    (e.g. a Mac mini with a USB webcam that has no mic). Because `getUserMedia`
 *    rejects the entire request when it can't provide the requested audio track,
 *    the camera appears "not found" even though it works. When audio was
 *    requested and we hit a missing-device/busy error, we retry VIDEO-ONLY — a
 *    silent clip is far better than no capture at all.
 *
 * Genuine failures that dropping audio cannot fix — permission denied
 * (`NotAllowedError`), or no camera even video-only — are rethrown so the caller
 * can show the correct guidance.
 */
export async function getUserMediaWithFallback(
  constraints: MediaStreamConstraints,
): Promise<MediaStream> {
  const md = navigator.mediaDevices;
  const wantsAudio = Boolean(constraints.audio);
  try {
    return await md.getUserMedia(constraints);
  } catch (err) {
    const name = (err as DOMException)?.name ?? "";

    // (1) Over-constrained video: relax to plain `video: true`, keep audio.
    if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError") {
      try {
        return await md.getUserMedia({ video: true, audio: constraints.audio ?? false });
      } catch (relaxErr) {
        // If the relaxed attempt still fails because the audio track can't be
        // provided, fall through to a final video-only attempt.
        const relaxName = (relaxErr as DOMException)?.name ?? "";
        if (wantsAudio && MISSING_DEVICE_ERRORS.has(relaxName)) {
          return await md.getUserMedia({ video: true, audio: false });
        }
        throw relaxErr;
      }
    }

    // (2) Missing/busy device WHILE audio was requested: the microphone is the
    // likely blocker. Retry video-only before giving up.
    if (wantsAudio && MISSING_DEVICE_ERRORS.has(name)) {
      return await md.getUserMedia({ video: constraints.video ?? true, audio: false });
    }

    throw err;
  }
}
